import React from 'react';
import ReactSpeedometer from 'react-d3-speedometer';
import Card from '../Card';
import { RiskScoreFootnote } from '../RiskScoreCards';
import { RISK_GAUGE_SEGMENT_STOPS, RISK_GAUGE_SEGMENT_COLORS } from '../../utils/riskColors';

// Shared risk-summary KPI cards for module dashboards (Ransomware/DarkWeb/AI/TPRM each keep their
// own copy of these; this shared version is used by newer dashboards instead of adding a 5th copy
// — see frontend/CLAUDE.md's note to prefer extraction over further duplication).

export const getRiskLevelColor = (level) => {
  switch (level) {
    case 'CRITICAL': return 'text-red-700 bg-red-100';
    case 'HIGH': return 'text-orange-600 bg-orange-100';
    case 'MEDIUM': return 'text-yellow-600 bg-yellow-100';
    case 'LOW': return 'text-green-600 bg-green-100';
    default: return 'text-gray-600 bg-gray-100';
  }
};

export function OverallRiskScoreCard({ analytics, title = 'Overall Risk Score' }) {
  const { overall_risk_score = 0, overall_RiskLevel = 'LOW', active_risks = 0, total_risks = 0 } = analytics || {};
  const hasAssessment = total_risks > 0;

  return (
    <Card className="text-center">
      <div className="mb-4">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">{title}</h3>
      </div>

      <div className="w-full flex justify-center mb-2">
        <ReactSpeedometer
          maxValue={100}
          value={hasAssessment ? overall_risk_score : 0}
          needleColor="#1f2937"
          customSegmentStops={RISK_GAUGE_SEGMENT_STOPS}
          segmentColors={RISK_GAUGE_SEGMENT_COLORS}
          segments={RISK_GAUGE_SEGMENT_COLORS.length}
          width={250}
          height={150}
          ringWidth={25}
          needleTransitionDuration={1000}
          needleTransition="easeElastic"
          currentValueText={hasAssessment ? `${overall_risk_score}%` : ' '}
          needleHeightRatio={0.5}
        />
      </div>

      {hasAssessment ? (
        <>
          <div className="flex justify-center space-x-2 mb-2">
            <div className={`text-xs px-2 py-1 rounded ${getRiskLevelColor(overall_RiskLevel)}`}>
              {overall_RiskLevel}
            </div>
          </div>
          <div className="text-xs text-gray-500">
            {active_risks} Active Risks
          </div>
          <RiskScoreFootnote />
        </>
      ) : (
        <div className="text-xs text-gray-400 italic">Assessment Not Started</div>
      )}
    </Card>
  );
}

export function PredictiveRiskTimelineCard({ predictiveRisk, loading, analytics }) {
  if (loading) {
    return (
      <Card className="text-center">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">
          Predictive Risk Timeline
        </h3>
        <div className="text-sm text-gray-500 py-6">Loading...</div>
      </Card>
    );
  }

  const { day45 = 0, day90 = 0, day180 = 0 } = predictiveRisk || {};
  const hasAssessment = (analytics?.total_risks || 0) > 0;

  return (
    <Card className="text-center">
      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">
        Predictive Risk Timeline
      </h3>

      <div className="space-y-3">
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-600">45 Days</span>
            <span className="font-medium text-yellow-700">{hasAssessment ? `${day45}%` : '—'}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-yellow-500 h-2 rounded-full transition-all duration-500"
              style={{ width: hasAssessment ? `${day45}%` : '0%' }}
            />
          </div>
        </div>

        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-600">90 Days</span>
            <span className="font-medium text-orange-700">{hasAssessment ? `${day90}%` : '—'}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-orange-500 h-2 rounded-full transition-all duration-500"
              style={{ width: hasAssessment ? `${day90}%` : '0%' }}
            />
          </div>
        </div>

        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-600">180 Days</span>
            <span className="font-medium text-red-700">{hasAssessment ? `${day180}%` : '—'}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-red-600 h-2 rounded-full transition-all duration-500"
              style={{ width: hasAssessment ? `${day180}%` : '0%' }}
            />
          </div>
        </div>
      </div>

      <div className="mt-4 text-xs text-gray-500">
        {hasAssessment ? 'Risk escalation forecast' : <span className="italic">Assessment Not Started</span>}
      </div>
    </Card>
  );
}

export function VulnerabilityDistributionCard({ analytics }) {
  const { risk_distribution = {}, total_risks = 0, active_risks = 0, remediated_risks = 0 } = analytics || {};
  const criticalCount = risk_distribution.CRITICAL || 0;
  const highCount = risk_distribution.HIGH || 0;
  const mediumCount = risk_distribution.MEDIUM || 0;
  const lowCount = risk_distribution.LOW || 0;
  const hasAssessment = total_risks > 0;

  return (
    <Card className="text-center">
      <div className="mb-4">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Vulnerability Distribution</h3>
      </div>

      {hasAssessment ? (
        <>
          <div className="space-y-2 mb-4">
            {criticalCount > 0 && (
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-red-700 rounded-full mr-2"></div>
                  <span className="text-sm font-medium">Critical</span>
                </div>
                <span className="text-sm font-bold text-red-700">{criticalCount}</span>
              </div>
            )}
            {highCount > 0 && (
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                  <span className="text-sm font-medium">High</span>
                </div>
                <span className="text-sm font-bold text-orange-600">{highCount}</span>
              </div>
            )}
            {mediumCount > 0 && (
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                  <span className="text-sm font-medium">Medium</span>
                </div>
                <span className="text-sm font-bold text-yellow-600">{mediumCount}</span>
              </div>
            )}
            {lowCount > 0 && (
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                  <span className="text-sm font-medium">Low</span>
                </div>
                <span className="text-sm font-bold text-green-600">{lowCount}</span>
              </div>
            )}
          </div>

          <div className="border-t pt-2 space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500">Total Risks</span>
              <span className="text-sm font-bold text-gray-800">{total_risks}</span>
            </div>
            {remediated_risks > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-xs text-green-600">Remediated</span>
                <span className="text-sm font-bold text-green-600">{remediated_risks}</span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-xs text-blue-600">Active Risks</span>
              <span className="text-sm font-bold text-blue-600">{active_risks}</span>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="space-y-2 mb-4">
            {[
              { label: 'Critical', dot: 'bg-red-200' },
              { label: 'High', dot: 'bg-orange-200' },
              { label: 'Medium', dot: 'bg-yellow-200' },
              { label: 'Low', dot: 'bg-green-200' },
            ].map(({ label, dot }) => (
              <div key={label} className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className={`w-3 h-3 ${dot} rounded-full mr-2`}></div>
                  <span className="text-sm font-medium text-gray-300">{label}</span>
                </div>
                <span className="text-sm font-bold text-gray-300">—</span>
              </div>
            ))}
          </div>
          <div className="border-t pt-2 text-xs text-gray-400 italic">Assessment Not Started</div>
        </>
      )}
    </Card>
  );
}
